import { Group, Flex, Text } from '@mantine/core';
import { BariatricProcedureStage, BariatricProcedureStageLabel, ProviderRole } from '@bariendo/lib/constants';
import { BARIATRIC_PROCEDURE_STAGE_EXTENSION } from '@bariendo/lib/extensions';
import { PROVIDER_ROLE_CODING_MAP } from '@bariendo/lib/codings';
import { Patient, Practitioner, PractitionerRole, Resource } from '@medplum/fhirtypes';
import { AsyncAutocompleteOption, ResourceAvatar } from '@medplum/react';
import { isResource, formatHumanName, getExtension, getReferenceString } from '@medplum/core';
import { MultipleResourcesSelector } from '@/components/MultipleResourcesSelector';
import { formatBirthDate } from '@/services/patient';

type GuestFormData = Patient | PractitionerRole;

interface GuestsSelectorProps {
  defaultValue?: GuestFormData[];
  disabled: boolean;
  provider: Practitioner | null;
  onChange: (resources: GuestFormData[] | undefined) => void;
}

export function GuestItemComponent({ resource }: { resource: GuestFormData }) {
  const avatarValue = isResource(resource, 'Patient')
    ? (resource as Patient)
    : (resource as PractitionerRole).practitioner;

  const name = isResource(resource, 'Patient')
    ? formatHumanName((resource as Patient).name?.[0])
    : (resource as PractitionerRole).practitioner?.display;

  let label = '';

  if (isResource(resource, 'Patient')) {
    const patient = resource as Patient;
    const stage = getExtension(patient, BARIATRIC_PROCEDURE_STAGE_EXTENSION)?.valueString as
      | BariatricProcedureStage
      | undefined;
    const stageLabel = stage ? BariatricProcedureStageLabel[stage] : undefined;
    const birthDate = patient.birthDate;

    const parts = ['Patient'];
    if (stageLabel) parts.push(stageLabel);
    if (birthDate) parts.push(formatBirthDate(birthDate));
    label = parts.join(' - ');
  } else {
    const code = (resource as PractitionerRole).code?.[0]?.coding?.[0]?.code;
    if (!code) {
      label = 'Provider';
    } else {
      const providerRole = PROVIDER_ROLE_CODING_MAP?.[code as ProviderRole];

      if (!providerRole) {
        label = 'Provider';
      } else {
        label = `Provider - ${providerRole.display}`;
      }
    }
  }

  return (
    <Group wrap="nowrap">
      <ResourceAvatar value={avatarValue} />
      <Flex direction="column">
        <Text>{name}</Text>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
      </Flex>
    </Group>
  );
}

function multipleResourceSelectorToOption<T extends Resource>(resource: T): AsyncAutocompleteOption<T> {
  const label = isResource(resource, 'PractitionerRole')
    ? ((resource as PractitionerRole).practitioner?.display ?? '')
    : formatHumanName((resource as Patient).name?.[0]);

  return {
    label,
    resource,
    value: getReferenceString(resource) ?? '',
  };
}

export function GuestsSelector({ onChange, disabled, provider, defaultValue }: GuestsSelectorProps) {
  /**
   * Get the search criteria for the guests
   * Exclude the practitioners of the provider from the search
   */
  const getSearchCriteriaForGuests = () => {
    if (!provider) {
      return {};
    }

    const providerReference = getReferenceString(provider);

    if (!providerReference) {
      return {};
    }

    return {
      searchCriteria: {
        PractitionerRole: {
          'practitioner:not': providerReference,
        },
      },
    };
  };

  return (
    <MultipleResourcesSelector
      name="guests"
      label="Guests"
      placeholder="Select guests"
      onChange={onChange}
      itemComponent={GuestItemComponent}
      disabled={disabled}
      resourceTypes={['Patient', 'PractitionerRole'] as const}
      toOption={multipleResourceSelectorToOption}
      defaultValue={defaultValue}
      {...getSearchCriteriaForGuests()}
    />
  );
}
